// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import fileDownload from "js-file-download";
import { useTranslation } from "react-i18next";

import {
  HvButton,
  HvTypography,
  HvLoading,
} from "@hitachivantara/uikit-react-core";
import {
  Backwards,
  Download,
  Forwards,
  Fullscreen,
  ZoomIn,
  ZoomOut,
  Info,
} from "@hitachivantara/uikit-react-icons";

import { Box } from "@material-ui/core";
import SwipeableViews from "react-swipeable-views";

import axios from "axios";
import Magnifier from "react-magnifier";

import useStyles from "./styles";

export interface CarouselProps {
  value: {
    name?: string;
    url: string;
    contentType: "video" | "image";
    props?: Record<string, unknown>;
  }[];
  fullscreen?: boolean;
  toggleFullScreen?: () => void;
  currentStep?: number;
  setCurrentStep?: (step: number) => void;
}

const Carousel: React.FC<CarouselProps> = ({
  value,
  fullscreen = false,
  toggleFullScreen,
  currentStep = 0,
  setCurrentStep,
}) => {
  const { t } = useTranslation("map");
  const classes = useStyles();

  const [activeStep, setActiveStep] = useState(currentStep);
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [zoom, setZoom] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [errorImages, setErrorImages] = useState<number[]>([]);

  const maxSteps = value.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  const toggleZoom = () => setZoom(!zoom);

  useEffect(() => {
    setUrl(value[activeStep].url);
    setName(value[activeStep].name ?? "download");
    setIsVideo(value[activeStep].contentType === "video");
    setZoom(false);
    setCurrentStep?.(activeStep);
  }, [value, activeStep, setCurrentStep]);

  const download = () => {
    setDownloading(true);
    axios
      .get(url, {
        responseType: "blob",
      })
      .then((res) => {
        fileDownload(res.data, name);
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  const onErrorHandler = useCallback(
    (index: number) => {
      const newArr: number[] = [...errorImages, index];
      setErrorImages(newArr);
    },
    [errorImages]
  );

  const mediaError = (id: number): boolean => {
    return errorImages.filter((i) => i === id).length > 0;
  };

  return (
    <div className={fullscreen ? classes.fullscreen : clsx(classes.root)}>
      <SwipeableViews
        index={activeStep}
        onChangeIndex={handleStepChange}
        className={clsx(
          classes.swipeContainer,
          fullscreen ? classes.fullscreenSwipeContainer : ""
        )}
      >
        {value.map((item, index) =>
          index === activeStep ? (
            <Box
              key={item.url}
              className={fullscreen ? classes.fullscreenStep : classes.step}
            >
              {mediaError(index) && (
                <div
                  className={clsx(
                    classes.unavailableContainer,
                    fullscreen ? "fullscreen" : ""
                  )}
                >
                  <Info
                    className={clsx(
                      classes.unavailableIcon,
                      fullscreen && "fullscreen"
                    )}
                  />
                  <HvTypography
                    className={clsx(
                      classes.unavailable,
                      fullscreen ? "fullscreen" : ""
                    )}
                    variant="mTitle"
                  >
                    {`${t("sections.details.properties.unavailable")} ${
                      item.contentType === "video"
                        ? t("sections.details.properties.video")
                        : t("sections.details.properties.image")
                    }`}
                  </HvTypography>
                  <HvTypography
                    className={clsx(
                      classes.unavailable,
                      fullscreen ? "fullscreen" : ""
                    )}
                    variant="normalText"
                  >
                    {t("sections.details.properties.unavailableMessage")}
                  </HvTypography>
                </div>
              )}
              {item.contentType === "image" &&
                !mediaError(index) &&
                (fullscreen && zoom ? (
                  <Magnifier
                    className={
                      fullscreen ? classes.fullscreenContent : classes.content
                    }
                    src={item.url}
                    mgShape="square"
                    mgWidth={200}
                    mgHeight={200}
                    width="inherit"
                    {...item.props}
                  />
                ) : (
                  <div>
                    <img
                      className={
                        fullscreen ? classes.fullscreenContent : classes.content
                      }
                      src={item.url}
                      alt={item.name}
                      {...item.props}
                      width="100%"
                      height="100%"
                      onError={() => onErrorHandler(index)}
                    />
                  </div>
                ))}
              {item.contentType === "video" && !mediaError(index) && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                  className={
                    fullscreen ? classes.fullscreenContent : classes.content
                  }
                  title={item.name}
                  controls
                  {...item.props}
                >
                  <source
                    src={item.url}
                    onError={() => onErrorHandler(index)}
                  />
                </video>
              )}
            </Box>
          ) : null
        )}
      </SwipeableViews>
      {!fullscreen && (
        <HvButton
          icon
          category="ghost"
          onClick={toggleFullScreen}
          className={classes.fullscreenIcon}
          id="fullscreenIcon"
        >
          <Fullscreen />
        </HvButton>
      )}
      <div className={fullscreen ? classes.fullscreenStepper : classes.stepper}>
        <HvButton
          className={fullscreen ? classes.fullscreenButton : classes.button}
          icon
          size="small"
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          <Backwards />
        </HvButton>
        <HvTypography
          className={
            fullscreen ? classes.fullscreenStepperCount : classes.stepperCount
          }
        >
          {`${activeStep + 1} / ${maxSteps}`}
        </HvTypography>
        <HvButton
          className={fullscreen ? classes.fullscreenButton : classes.button}
          icon
          size="small"
          onClick={handleNext}
          disabled={activeStep === maxSteps - 1}
        >
          <Forwards />
        </HvButton>
      </div>
      {fullscreen && (
        <>
          <div className={classes.controls}>
            {downloading && (
              <div className={classes.loadingContainer}>
                <HvLoading small color="atmo5" />
                <HvTypography className={classes.loading}>
                  {t("sections.details.properties.downloading")}
                </HvTypography>
              </div>
            )}
            <HvButton
              icon
              className={fullscreen ? classes.fullscreenButton : classes.button}
            >
              <Download onClick={download} />
            </HvButton>
            <HvButton
              icon
              className={fullscreen ? classes.fullscreenButton : classes.button}
              disabled={isVideo}
            >
              {zoom ? (
                <ZoomOut onClick={toggleZoom} className={classes.selected} />
              ) : (
                <ZoomIn onClick={!isVideo ? toggleZoom : undefined} />
              )}
            </HvButton>
          </div>
          <div
            onClick={activeStep !== 0 ? handleBack : undefined}
            className={clsx(classes.sideButton, classes.sideBackButton)}
            role="button"
            aria-label="Back"
            tabIndex={0}
            onKeyDown={activeStep !== 0 ? handleBack : undefined}
          />
          <div
            onClick={activeStep !== maxSteps - 1 ? handleNext : undefined}
            className={clsx(classes.sideButton, classes.sideNextButton)}
            role="button"
            aria-label="Back"
            tabIndex={0}
            onKeyDown={activeStep !== maxSteps - 1 ? handleNext : undefined}
          />
        </>
      )}
    </div>
  );
};

export default Carousel;
